import { useEffect, useRef, useState } from 'react';
import type { PlanSlug } from '@/api/types';
import { track } from '@/analytics/tracker';
import { Dialog, type DialogCloseReason } from '@/app/layers/Dialog';
import { useToast } from '@/app/toasts/ToastProvider';
import { useAuth } from '@/auth/AuthProvider';
import { useT } from '@/i18n/LocaleProvider';
import type { UiKey } from '@/i18n/dictionary';
import { cx } from '@/lib/cx';
import {
  checkoutConflictHintFor,
  describeCheckoutConflict,
  paidSubscriptionOf,
} from './billingState';
import type { PaywallIntent } from './PaywallContext';
import { paywallHeadlineKey } from './paywallHeadline';
import { PlanCards } from './PlanCards';
import styles from './Paywall.module.css';
import { useCheckout } from './useCheckout';
import { DEFAULT_PAYWALL_PLAN } from './usePaywallPrice';
import { useSubscriptionPlans } from './useSubscriptionPlans';

interface Props {
  intent: PaywallIntent;
  onClose: () => void;
}

/** Окно подписки 640px (BILL-2, README §7): тексты из прототипа AppV2. */
export function PaywallModal({ intent, onClose }: Props) {
  const t = useT();
  const { user } = useAuth();
  const prices = useSubscriptionPlans();
  const toast = useToast();
  const [plan, setPlan] = useState<PlanSlug>(intent.plan ?? DEFAULT_PAYWALL_PLAN);
  const checkout = useCheckout({
    onActivated: () => {
      toast.show(t('paywall.active'));
    },
  });
  const autoStarted = useRef(false);
  const statusRef = useRef(checkout.status);
  statusRef.current = checkout.status;
  const correlationRef = checkout.correlationRef;
  // Датчики закрытия (B6): чем закрыли, сколько окно жило, какой план был выбран.
  const planRef = useRef(plan);
  planRef.current = plan;
  const intentRef = useRef(intent);
  intentRef.current = intent;
  const openedAtRef = useRef(Date.now());
  const closeReasonRef = useRef<string | null>(null);
  // Живая платная строка уже есть (грант с более поздним сроком прячет её в
  // has_subscription, пауза и дунинг доступа не дают): Subscribe не рисуем вовсе —
  // серверный guard 409 остаётся последней линией, а не первой.
  const conflictHint = checkoutConflictHintFor(paidSubscriptionOf(user));
  // Заголовок называет то, во что упёрлись (строка реестра), — общий только без своей причины.
  const headlineKey = paywallHeadlineKey(intent);

  useEffect(() => {
    track('subscription_modal_open', {
      metadata: {
        trigger: intent.autoStart ? 'auto' : 'manual',
        initial_plan: intent.plan ?? null,
        source: intent.source,
        entry: intent.trigger,
      },
    });
    return () => {
      // close_reason 'auto' — окно сняли без действия человека (доступ появился).
      track('subscription_modal_close', {
        correlation_id: correlationRef.current ?? undefined,
        metadata: {
          final_status: statusRef.current,
          close_reason: closeReasonRef.current ?? 'auto',
          ms_open: Date.now() - openedAtRef.current,
          plan_selected: planRef.current,
          entry: intentRef.current.trigger,
          source: intentRef.current.source,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Намерение с лендинга: план уже выбран там — сразу в Paddle (BILL-3).
  // При живой платной строке чекаут не открываем — на экране подсказка вместо Subscribe.
  useEffect(() => {
    if (!intent.autoStart || !intent.plan || autoStarted.current || conflictHint) return;
    autoStarted.current = true;
    void checkout.start(intent.plan);
  }, [intent, checkout, conflictHint]);

  const busy = checkout.isBusy;
  const close = (reason?: DialogCloseReason | 'not_now' | 'activated') => {
    if (busy) return;
    closeReasonRef.current = reason ?? 'escape';
    onClose();
  };

  return (
    <Dialog
      open
      onClose={close}
      labelledBy="paywall-title"
      maxWidth={640}
      elevated
      className={styles.panel}
    >
      <p className={styles.eyebrow}>{t('paywall.eyebrow')}</p>
      <h2
        id="paywall-title"
        className={cx(styles.title, headlineKey !== 'paywall.title' && styles.titleEntry)}
      >
        {t(headlineKey)}
      </h2>
      <p className={styles.lead}>{t('paywall.lead')}</p>
      {intent.reason ? (
        <p className={styles.reason} role="note">
          {t(`paywall.reason.${intent.reason}` as UiKey, {
            limit: user?.quota?.detail_views.limit ?? 7,
          })}
        </p>
      ) : null}

      {checkout.status === 'processing' ? (
        <p className={styles.processing} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {t('paywall.activating')}
        </p>
      ) : checkout.status === 'activated' ? (
        <div className={styles.activated} role="status">
          <p className={styles.activatedTitle}>{t('paywall.active')}</p>
          <button type="button" className={styles.primary} onClick={() => close('activated')}>
            {t('paywall.startExploring')}
          </button>
        </div>
      ) : conflictHint ? (
        <>
          <p className={styles.reason} role="note">
            {describeCheckoutConflict(conflictHint, 'modal')}
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.ghost} onClick={() => close('not_now')}>
              {t('common.notNow')}
            </button>
          </div>
        </>
      ) : (
        <>
          <PlanCards prices={prices} value={plan} onChange={setPlan} disabled={busy} />
          {checkout.error ? (
            <p className={styles.error} role="alert">
              {checkout.error}
            </p>
          ) : null}
          <div className={styles.actions}>
            <button
              type="button"
              className={cx(styles.primary, busy && styles.busy)}
              onClick={() => void checkout.start(plan)}
              disabled={busy}
            >
              {checkout.status === 'opening' ? t('paywall.opening') : t('paywall.continue')}
            </button>
            <button
              type="button"
              className={styles.ghost}
              onClick={() => close('not_now')}
              disabled={busy}
            >
              {t('common.notNow')}
            </button>
          </div>
          <p className={styles.note}>{t('paywall.note')}</p>
        </>
      )}
    </Dialog>
  );
}
